import {
    collection,
    query,
    where,
    onSnapshot,
    orderBy,
    serverTimestamp,
    updateDoc,
    doc,
    getDoc,
    getDocs,
    writeBatch
} from 'firebase/firestore';
import { db, appId } from './config/firebase';

/**
 * Real-time Notification Service
 * Handles notifications between teachers and students
 */
class NotificationService {
    constructor() {
        this.listeners = new Map(); // Store active listeners
    }

    /**
     * Send notification to specific students in a rombel
     * @param {string} rombelId - Target rombel ID
     * @param {string} senderId - Sender user ID (usually teacher)
     * @param {string} senderName - Sender display name
     * @param {string} type - Notification type: 'announcement', 'assignment', 'reminder', 'chat'
     * @param {string} title - Notification title
     * @param {string} message - Notification content
     * @param {Object} data - Additional data (assignment details, etc.)
     * @param {Array} targetStudents - Array of student IDs (optional, defaults to all rombel students)
     */
    async sendNotification({
        rombelId,
        senderId,
        senderName,
        type,
        title,
        message,
        data = {},
        targetStudents = null
    }) {
        try {

            // Get target students if not specified
            let studentIds = targetStudents;
            if (!studentIds) {
                studentIds = await this.getRombelStudents(rombelId);
            }

            const batch = writeBatch(db);
            const notifications = [];

            // Create notification for each student
            for (const studentId of studentIds) {
                const notificationRef = doc(collection(db, `artifacts/${appId}/users/${studentId}/notifications`));

                const notificationData = {
                    id: notificationRef.id,
                    rombelId,
                    senderId,
                    senderName,
                    type,
                    title,
                    message,
                    data,
                    isRead: false,
                    createdAt: serverTimestamp(),
                    readAt: null
                };

                batch.set(notificationRef, notificationData);
                notifications.push({
                    studentId,
                    notificationId: notificationRef.id,
                    ...notificationData
                });
            }

            // Also store in global notifications for admin/analytics
            const globalNotificationRef = doc(collection(db, `artifacts/${appId}/notifications`));
            batch.set(globalNotificationRef, {
                id: globalNotificationRef.id,
                rombelId,
                senderId,
                senderName,
                type,
                title,
                message,
                data,
                targetStudents: studentIds,
                sentAt: serverTimestamp(),
                deliveryCount: studentIds.length
            });

            await batch.commit();

            return {
                success: true,
                notificationCount: studentIds.length,
                notifications
            };

        } catch (error) {
            console.error('❌ Error sending notification:', error);
            throw error;
        }
    }

    /**
     * Send announcement to all students in rombel
     */
    async sendAnnouncement(rombelId, senderId, senderName, title, message) {
        return this.sendNotification({
            rombelId,
            senderId,
            senderName,
            type: 'announcement',
            title,
            message,
            data: {
                icon: '📢',
                priority: 'high'
            }
        });
    }

    /**
     * Send assignment notification
     */
    async sendAssignmentNotification(rombelId, senderId, senderName, assignmentData) {
        const { type, title, deadline, description } = assignmentData;

        return this.sendNotification({
            rombelId,
            senderId,
            senderName,
            type: 'assignment',
            title: `📝 ${type}: ${title}`,
            message: `Deadline: ${deadline}\n${description}`,
            data: {
                assignmentId: assignmentData.id,
                assignmentType: type,
                deadline,
                icon: type === 'soal' ? '📝' : '📋',
                priority: 'high',
                actionRequired: true
            }
        });
    }

    /**
     * Send chat message notification
     */
    async sendChatNotification(rombelId, senderId, senderName, message) {
        return this.sendNotification({
            rombelId,
            senderId,
            senderName,
            type: 'chat',
            title: `💬 Pesan dari ${senderName}`,
            message,
            data: {
                icon: '💬',
                priority: 'normal'
            }
        });
    }

    /**
     * Get all students in a rombel
     */
    async getRombelStudents(rombelId) {
        try {
            const rombelRef = doc(db, `artifacts/${appId}/rombels`, rombelId);
            const rombelDoc = await getDoc(rombelRef);

            if (rombelDoc.exists()) {
                const rombelData = rombelDoc.data();
                return rombelData.siswaIds || [];
            }

            return [];
        } catch (error) {
            console.error('Error getting rombel students:', error);
            return [];
        }
    }

    /**
     * Listen to notifications for a specific user
     * @param {string} userId - User ID to listen for
     * @param {Function} callback - Callback function to handle notifications
     * @returns {Function} - Unsubscribe function
     */
    listenToNotifications(userId, callback) {
        const notificationsRef = collection(db, `artifacts/${appId}/users/${userId}/notifications`);
        const q = query(
            notificationsRef,
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notifications = [];
            snapshot.docs.forEach(doc => {
                notifications.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            // Separate unread notifications
            const unreadNotifications = notifications.filter(n => !n.isRead);

            callback({
                all: notifications,
                unread: unreadNotifications,
                unreadCount: unreadNotifications.length
            });
        });

        // Store listener for cleanup
        this.listeners.set(`notifications_${userId}`, unsubscribe);
        return unsubscribe;
    }

    /**
     * Mark notification as read
     */
    async markAsRead(userId, notificationId) {
        try {
            const notificationRef = doc(db, `artifacts/${appId}/users/${userId}/notifications/${notificationId}`);
            await updateDoc(notificationRef, {
                isRead: true,
                readAt: serverTimestamp()
            });
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }

    /**
     * Mark all notifications as read for a user
     */
    async markAllAsRead(userId) {
        try {
            const notificationsRef = collection(db, `artifacts/${appId}/users/${userId}/notifications`);
            const q = query(notificationsRef, where('isRead', '==', false));
            const snapshot = await getDocs(q);

            const batch = writeBatch(db);
            snapshot.docs.forEach(doc => {
                batch.update(doc.ref, {
                    isRead: true,
                    readAt: serverTimestamp()
                });
            });

            await batch.commit();
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    }

    /**
     * Send notification to rombel (shorthand method for assignments)
     * Used by BuatSoalPage and other components
     */
    async sendToRombel(rombelId, assignmentData) {
        try {
            const { id, type, title, deadline, description } = assignmentData;

            return this.sendNotification({
                rombelId,
                senderId: assignmentData.senderId || 'system',
                senderName: assignmentData.senderName || 'Guru',
                type: 'assignment',
                title: `📝 ${type || 'Soal'}: ${title}`,
                message: deadline ? `Deadline: ${new Date(deadline).toLocaleDateString('id-ID')}\n${description || ''}` : description || '',
                data: {
                    assignmentId: id,
                    assignmentType: type || 'soal',
                    deadline,
                    icon: type === 'soal' ? '📝' : '📋',
                    priority: 'high',
                    actionRequired: true,
                    url: type === 'soal' ? `/siswa_soal` : undefined
                }
            });
        } catch (error) {
            console.error('❌ Error in sendToRombel:', error);
            throw error;
        }
    }

    /**
     * Clean up all listeners
     */
    cleanup() {
        this.listeners.forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        });
        this.listeners.clear();
    }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default NotificationService;
